import {Scissors, Wrench, Truck, HeadphonesIcon, Settings, CheckCircle} from 'lucide-react';

const services = [
  {
    icon: Scissors,
    title: "Corte e Montagem",
    desc: "Confecção de mangueiras hidráulicas por medida com serviço de corte e montagem de terminais no local.",
    highlight: true,
  },
  {
    icon: Wrench,
    title: "Manutenção Industrial",
    desc: "Suporte técnico para identificação e substituição de componentes hidráulicos e pneumáticos.",
    highlight: false,
  },
  {
    icon: Truck,
    title: "Pronta Entrega",
    desc: "Estoque amplo para entrega imediata. Atendemos balcão e delivery para toda a região.",
    highlight: false,
  },
  {
    icon: HeadphonesIcon,
    title: "Consultoria Técnica",
    desc: "Assessoria especializada na escolha do produto correto para cada aplicação industrial.",
    highlight: false,
  },
  {
    icon: Settings,
    title: "Fabricação Especial",
    desc: "Desenvolvimento de mangueiras e conexões especiais conforme especificação técnica do cliente.",
    highlight: false,
  },
  {
    icon: CheckCircle,
    title: "Controle de Qualidade",
    desc: "Todos os produtos são testados e certificados para garantir segurança e desempenho.",
    highlight: false,
  },
];

export default function Services() {
  return (
    <section id="servicos" className="bg-[#0D1B3E] py-24 md:py-32 relative overflow-hidden">

      {/* Ambient glows */}
      <div className="absolute top-0 right-0 w-[30rem] h-[30rem] bg-[#F5A623]/6 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[24rem] h-[24rem] bg-[#F5A623]/4 rounded-full blur-[100px] pointer-events-none" />

      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "linear-gradient(rgba(245,166,35,1) 1px, transparent 1px), linear-gradient(90deg, rgba(245,166,35,1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">

        {/* ── Header ── */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div>
            <span className="inline-flex items-center gap-3 text-[#F5A623] font-black uppercase tracking-[0.25em] text-xs mb-4">
              <span className="w-10 h-px bg-[#F5A623]" />
              O que Oferecemos
            </span>
            <h2 className="font-barlow-condensed text-5xl md:text-7xl font-black text-white uppercase leading-[0.9]">
              Nossos Serviços
            </h2>
            <p className="mt-4 text-gray-400 text-lg max-w-lg leading-relaxed font-light">
              Além do fornecimento de produtos, oferecemos serviços especializados para atender toda a demanda industrial.
            </p>
          </div>
          <div className="h-px flex-1 max-w-xs bg-gradient-to-r from-[#F5A623]/40 to-transparent hidden md:block mb-4" />
        </div>

        {/* ── Grid ── */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map(({ icon: Icon, title, desc, highlight }) => (
            <div
              key={title}
              className={`card-lift relative rounded-2xl p-8 border overflow-hidden group ${
                highlight
                  ? "bg-gradient-to-br from-[#F5A623] to-[#d4890e] border-transparent shadow-2xl shadow-[#F5A623]/20"
                  : "bg-white/[0.04] border-white/8 hover:bg-white/[0.08] hover:border-[#F5A623]/30 hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)]"
              }`}
            >
              {/* Top accent for non-highlight */}
              {!highlight && (
                <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#F5A623]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              )}

              <div
                className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 ${
                  highlight ? "bg-[#0D1B3E]/20" : "bg-[#F5A623]/10"
                }`}
              >
                <Icon
                  size={24}
                  className={highlight ? "text-[#0D1B3E]" : "text-[#F5A623]"}
                />
              </div>

              <h3
                className={`font-bold text-lg mb-3 tracking-tight ${
                  highlight ? "text-[#0D1B3E]" : "text-white"
                }`}
              >
                {title}
              </h3>
              <p
                className={`text-sm leading-relaxed ${
                  highlight ? "text-[#0D1B3E]/75" : "text-gray-400"
                }`}
              >
                {desc}
              </p>

              {highlight && (
                <span className="absolute top-5 right-5 bg-[#0D1B3E]/15 text-[#0D1B3E] text-[9px] font-black uppercase tracking-[0.2em] px-2.5 py-1 rounded-full">
                  Destaque
                </span>
              )}
            </div>
          ))}
        </div>

        {/* ── CTA Banner ── */}
        <div className="mt-12 relative overflow-hidden bg-gradient-to-r from-[#F5A623] to-[#d4890e] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl shadow-[#F5A623]/15">
          <div className="absolute -right-12 -top-12 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
          <div>
            <h3 className="text-[#0D1B3E] font-black text-2xl md:text-3xl font-barlow-condensed uppercase">
              Atendimento Especializado
            </h3>
            <p className="text-[#0D1B3E]/75 mt-1 text-base font-light">
              Precisa de suporte técnico? Fale agora com nossa equipe especializada.
            </p>
          </div>
          <a
            href="https://wa.me/5521973516085?text=Preciso de atendimento especializado!"
            target="_blank"
            rel="noopener noreferrer"
            className="relative z-10 whitespace-nowrap bg-[#0D1B3E] hover:bg-[#162952] text-white font-black px-9 py-4 rounded-xl uppercase tracking-widest text-sm transition-all duration-200 hover:-translate-y-0.5 shadow-xl shadow-[#0D1B3E]/30"
          >
            Falar com Especialista
          </a>
        </div>
      </div>
    </section>
  );
}
